import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import some from 'lodash/some'
import find from 'lodash/find'

import { AvatarInfo, Button, Paragraph, Text } from 'components/shared'
import {
  ClientsNestedDropdown,
  IUserOption,
  PropertyValuesList,
  TagDropdown,
  UserDropdown,
} from 'components/SingleTask/taskAside'
import ArchiveTask from 'components/SingleTask/taskAside/ArchiveTask'
import { TaskAction, TaskData, TaskDataList, TaskNotice, Wrapper } from '../TaskAside.style'

import {
  getCategoriesAction,
  getClientsAction,
  getStatusesAction,
  getTaskAction,
  patchTaskAction,
  postTaskAction,
} from 'store/taskManager/actions'
import { IPostTask, StatusTypeIds } from 'store/taskManager/model'
import {
  Box,
  COMMON_LINKS,
  IUser,
  ShowMore,
  Switch,
  Tag,
  TUserTag,
  useFetch,
  withTags,
  Link,
  AppContext,
} from '@centra/centra-ui'
import {
  buildOldStyleClientList,
  fetchClientAccountList,
  fetchClientList,
  fetchClientRelationLinkList,
  fetchReportCompanyForOldId,
  getUniqueParticipantList,
} from '../utils'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import { ArchivedIcon } from '../../../taskManager/Task/_ArchivedIcon/ArchivedIcon'
import { TaskCompany } from '../../../TaskCompany'
import { Roles } from 'utils'

interface IAdminAside {
  name: string
  isShowExtraMessages: boolean
  onClose: () => void
  onChangeShowExtraMessages: () => void
}

const deepClientSearch = (arr: any[], id: any) => {
  const res: any = arr.map(o => {
    if (o.id === id) {
      const client = { ...o }
      // Если это агентство, то не надо возвращать список клиентов,
      // так как если передать, то в левой панели будет показано,
      // как будто выбран клиент агентства. Для рекламодателя ничего не произойдет
      delete client.clients
      return client
    } else if (o.clients) {
      const deepRes = deepClientSearch(o.clients, id)
      return deepRes.length ? { ...o, clients: deepRes } : false
    }
  })
  return res.filter((o: any) => o)
}

/**
 * ВАЖНО!!!!
 * ВАЖНО!!!!
 * ВАЖНО!!!! Тут только аккаунты, но не админы, РАМ-ы или клиенты, для этого иди в ClientAside
 * ВАЖНО!!!!
 * ВАЖНО!!!!
 */
const AccountAside = (props: IAdminAside) => {
  const { setNotification } = useContext(AppContext)
  const { name, isShowExtraMessages, onClose, onChangeShowExtraMessages } = props
  const dispatch = useDispatch()
  const { categories, statuses, activeTask, profile, user } = useSelector(({ taskManager, profile, auth }) => ({
    categories: taskManager.categories.filter(category => category.active),
    statuses: taskManager.statuses,
    activeTask: taskManager.task,
    profile: profile.profile,
    user: auth.user,
  }))

  const [clients, setClients] = useState<any>([])
  const [clientManagers, setClientManagers] = useState<IUserOption[]>([])
  const [isSaved, setIsSaved] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [assignedToOptions, setAssignedToOptions] = useState<IUserOption[]>([])
  const [accountManagerOptions, setAccountManagerOptions] = useState<IUserOption[]>([])
  const [clientId, setClientId] = useState<number | null>(null)
  const [task, setTask] = useState<IPostTask>({
    name: '',
    category: null,
    status: null,
    client: null,
    clientManager: null,
    assigned: null,
  })

  const isValid = useMemo(() => {
    const test: any = { ...task }
    return !some(Object.keys(test), function (key: any) {
      if (key === 'name') {
        return test[key].trim().length < 3 || test[key].trim().length > 60
      }
      return !test[key]
    })
  }, [task])
  const canBeArchived = useMemo(() => {
    return isEditing && (task.status?.id === 4 || task.status?.id === 5)
  }, [isEditing, user, task])

  // Является ли пользователь финансовым координатором
  const isFinCoordinator = withTags({ tags: 'role.finCoordinator' as TUserTag, user: user as IUser })
  // Является ли пользователь автором задачи
  const isTaskAuthor = task.author?.id === user?.id

  const participantsList = useMemo(() => {
    if (isFinCoordinator) {
      if (task.lastCommunicationUser) {
        return getUniqueParticipantList([...(task.participants || []), ...[task.lastCommunicationUser]])
      }
      return task.participants || []
    }
    return getUniqueParticipantList([...(task.participants || []), ...(task.finCoordinators || [])])
  }, [isFinCoordinator, task])

  const doFetchReportCompanyForOldId = useFetch({
    cb: fetchReportCompanyForOldId,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о клиенте',
    },
  })
  useEffect(() => {
    if (activeTask?.client?.id) {
      doFetchReportCompanyForOldId.fetch(activeTask.client.id)
    }
  }, [activeTask])

  const clientLink = useMemo(() => {
    if (activeTask) {
      const {
        client: { id },
      } = activeTask

      return `${COMMON_LINKS.CUSTOMER.href}/client/${id}`
    }
    return ''
  }, [activeTask, doFetchReportCompanyForOldId.result])

  const currentStatuses = useMemo(() => {
    const clientRoles: Roles[] = [
      Roles.CLIENT_MANAGER,
      Roles.CLIENT_PRIME_MANAGER,
      Roles.AGENCY_MANAGER,
      Roles.AGENCY_PRIME_MANAGER,
    ]

    if (clientRoles.includes(task.author?.roles?.[0])) {
      return statuses.filter(s => s.id !== StatusTypeIds.onConfirmed)
    }

    return statuses
  }, [statuses, task.author?.roles])

  const participantItems = useMemo(() => {
    return task.participants?.map(item => (
      <Box key={item.id} marginBottom={12}>
        <AvatarInfo
          size={'md'}
          avatar={item.avatar}
          firstName={item.firstName}
          lastName={item.lastName}
          storage={false}
          grayscale
        >
          <Paragraph size={'sm'} weight={'bold'}>
            {item.firstName} {item.lastName}
          </Paragraph>
          <Paragraph className={'grey'} size={'sm'}>
            {item.title}
          </Paragraph>
        </AvatarInfo>
      </Box>
    ))
  }, [task.participants])

  useEffect(() => {
    dispatch(getCategoriesAction())
    dispatch(getStatusesAction())
    dispatch(getClientsAction())
  }, [])

  useEffect(() => {
    if (task.name === name) return
    const newTask = { ...task, name: name.replace(/ +/g, ' ').trim() }
    setTask(newTask)
    if (isEditing) {
      patchTask(newTask)
    }
  }, [name])

  useEffect(() => {
    if (isEditing) {
      setAssignedToOptions(participantsList)
    } else {
      if (profile) {
        setAccountManagerOptions([profile])
        setAssignedToOptions([profile])
      }

      if (profile && task.clientManager) {
        setAssignedToOptions([profile, task.clientManager])
      }
    }
  }, [profile, task.participants, task.clientManager, isEditing])

  useEffect(() => {
    if (!activeTask && assignedToOptions.length) {
      setTask({ ...task, assigned: assignedToOptions[0] })
    }
  }, [assignedToOptions])

  useEffect(() => {
    if (!activeTask && clientManagers.length) {
      setTask({ ...task, clientManager: clientId ? clientManagers[0] : null })
    }
  }, [clientManagers])

  const doFetchClientList = useFetch({
    cb: fetchClientList,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о клиентах',
    },
  })

  const doFetchClientRelationLinkList = useFetch({
    cb: fetchClientRelationLinkList,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о связях клиентов',
    },
  })

  const doFetchClientAccountList = useFetch({
    cb: fetchClientAccountList,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о менеджерах клиента',
    },
  })

  useEffect(() => {
    doFetchClientList.fetch()
    doFetchClientRelationLinkList.fetch()
  }, [])

  useEffect(() => {
    // Весь этот код перемешивания в один объект нужен только на время,
    // пока не перейдем на нормальный древовидный select/input, с постепенной загрузкой по требованию
    const clientList = doFetchClientList.result?.data?.page?.entries || []
    const clientRelationLinkList = doFetchClientRelationLinkList.result?.data?.page?.entries || []
    const newClients = buildOldStyleClientList(clientRelationLinkList, clientList)

    setClients(newClients)
  }, [doFetchClientList.result, doFetchClientRelationLinkList.result])

  useEffect(() => {
    if (clientId) {
      doFetchClientAccountList.fetch(clientId)
    }
  }, [clientId])

  useEffect(() => {
    const accountList = doFetchClientAccountList.result?.data?.page?.entries || []
    // Строим список такой же как он получался из саги
    const newClientManagers = accountList.map((item): IUserOption => {
      return {
        id: item.accountId,
        firstName: item.accountFirstName,
        lastName: item.accountLastName,
        title: '',
        avatar: '',
      }
    })
    setClientManagers(newClientManagers)
  }, [doFetchClientAccountList.result])

  useEffect(() => {
    if (activeTask) {
      setIsEditing(true)
      const {
        id,
        name,
        category,
        status,
        assigned,
        client,
        participants,
        finCoordinators,
        lastCommunicationUser,
        author,
        isClientConfirm,
      } = activeTask
      setTask({
        id,
        name,
        category,
        status,
        assigned,
        client,
        participants,
        finCoordinators,
        lastCommunicationUser,
        author,
        clientManager: null,
        isClientConfirm,
      })
    }
  }, [activeTask])

  const onChangeCategory = (id: number) => {
    if (task?.category?.id !== id) {
      const newTask = { ...task, category: find(categories, { id }) || null }
      setTask(newTask)
      if (isEditing) {
        patchTask(newTask)
      }
    }
  }

  const onChangeStatus = (id: number) => {
    if (isDisableChangeStatus && id === StatusTypeIds.done) {
      setNotification({
        view: 'error',
        title: 'Задача не согласована клиентом',
        content: 'Перевести задачу в статус Готово можно только после согласования клиентом в тикете',
      })
      return
    }

    if (task?.status?.id !== id) {
      const newTask = { ...task, status: find(statuses, { id }) || null }
      setTask(newTask)
      if (isEditing) {
        patchTask(newTask)
      }
    }
  }

  const onChangeClient = (id: number) => {
    setClientId(id)
    const selectedClient = deepClientSearch(clients, id)[0] || null
    setTask({ ...task, client: selectedClient })
  }

  const onChangeAssigned = (id: number) => {
    if (task && task.assigned?.id !== id) {
      const newTask = { ...task, assigned: find(assignedToOptions, { id }) || null }
      setTask(newTask)
      if (isEditing) {
        patchTask(newTask)
      }
    }
  }

  const onChangeAccountManager = (id: number) => {
    console.info('onChangeAccountManager', id)
  }

  const onChangeClientManager = (id: number) => {
    setTask({ ...task, clientManager: find(clientManagers, { id }) || null })
  }

  const onCreateTask = () => {
    setSubmitting(true)
    dispatch(
      postTaskAction(task, ({ success }) => {
        setSubmitting(false)
        if (success) onClose()
      })
    )
  }

  const patchTask = (task: IPostTask) => {
    dispatch(
      patchTaskAction(task, ({ success }) => {
        if (success) {
          dispatch(
            getTaskAction({
              id: task.id!,
              refreshCurrentTask: true,
            })
          )
          setIsSaved(true)
          setTimeout(() => {
            setIsSaved(false)
          }, 3000)
        }
      })
    )
  }

  const participants = () => {
    if (isEditing && participantItems) {
      return (
        <Box column>
          <ShowMore htmlContent={participantItems} maxItems={3} btnSize={'sm'} isToggle />
        </Box>
      )
    }
    return (
      <>
        {accountManagerOptions.length && (
          <UserDropdown
            list={accountManagerOptions}
            value={accountManagerOptions[0] || null}
            onChange={onChangeAccountManager}
          />
        )}
        {!!clientManagers.length && clientId && (
          <UserDropdown list={clientManagers} value={task.clientManager || null} onChange={onChangeClientManager} />
        )}
      </>
    )
  }

  //Если есть агентство, то отображаем время архивирования агентства, иначе - клиента
  const archivedTime = activeTask?.client?.agencyArchivedAt || activeTask?.client?.archivedAt
  const archivedClientKind =
    !activeTask?.client?.agencyArchivedAt && activeTask?.client?.kind === 'agencyClient' ? 'Клиент агентства' : 'Клиент'
  const showClientArchived = !activeTask?.client?.agencyArchivedAt && activeTask?.client?.archivedAt

  // Если задача на клиенте, в статусе "Ожидает согласования" и несогласована клиентом,
  // доступа у аккаунта к изменению статуса нет
  const isDisableChangeStatus =
    task.status?.id === StatusTypeIds.onConfirmed && task.assigned?.id !== user?.id && !task.isClientConfirm

  return (
    <Wrapper>
      <TaskDataList>
        {isFinCoordinator && !isTaskAuthor ? (
          <TaskData>
            <label>категория</label>
            <Box style={{ marginLeft: '-8px' }}>
              <Tag id={`task_${task.id}_category_tag`} size={'sm'} variant={'outline'} view={task.category?.colorAlias}>
                {task.category?.name}
              </Tag>
            </Box>
          </TaskData>
        ) : (
          <TagDropdown
            label={'категория'}
            tagType={'category'}
            list={categories}
            value={task.category}
            onChange={onChangeCategory}
          />
        )}
        <TagDropdown
          label={'Статус'}
          tagType={'status'}
          list={currentStatuses}
          value={task.status}
          onChange={onChangeStatus}
        />

        {activeTask ? (
          <TaskData style={{ lineHeight: 1 }}>
            <label>Клиент</label>
            <Link id={'clientLink'} href={clientLink}>
              <TaskCompany
                client={activeTask.client}
                fontSize={'caption'}
                fontWight={'semiBold'}
                fontColor={'basicBlack'}
              />
            </Link>
          </TaskData>
        ) : (
          <ClientsNestedDropdown menuItems={clients} value={task.client} onChange={onChangeClient} />
        )}
        <TaskData>
          <label>участники</label>
          {participants()}
        </TaskData>

        {!!assignedToOptions.length && (
          <TaskData>
            <label>Исполнитель</label>
            <UserDropdown list={assignedToOptions} value={task.assigned || null} onChange={onChangeAssigned} />
          </TaskData>
        )}
        <TaskData>
          <label>Номер задачи</label>
          <Paragraph size={'sm'}>
            <b>{task.id}</b>
          </Paragraph>
        </TaskData>

        <PropertyValuesList />
      </TaskDataList>

      {canBeArchived && task.id && <ArchiveTask taskId={task.id} onClose={onClose} />}

      <TaskAction>
        {!isEditing && (
          <Button onClick={onCreateTask} disabled={!isValid} $loading={submitting}>
            Создать задачу
          </Button>
        )}
        {isSaved && (
          <TaskNotice>
            <Text size={'sm'}>Изменения сохранены</Text>
            <svg width="16px" height="16px" style={{ verticalAlign: 'bottom' }}>
              <use xlinkHref="#check" />
            </svg>
          </TaskNotice>
        )}
      </TaskAction>

      {isEditing && (
        <Box>
          <Switch id={'showExtraMessagesSwitch'} value={isShowExtraMessages} onChange={onChangeShowExtraMessages} />
          <Box marginLeft={8}>
            <Text size={'sm'}>Показать больше истории тикета</Text>
          </Box>
        </Box>
      )}
    </Wrapper>
  )
}

export default AccountAside
